import { useState, useEffect, useRef, useCallback } from 'react';
import { getSignedUrl, parseS3Url } from '../utils/awsConfig';

interface UseSignedImageResult {
  signedUrl: string | null;
  loading: boolean;
  error: string | null;
}

// Global cache to persist across component re-renders and re-mounts
const globalSignedUrlCache = new Map<string, UseSignedImageResult>();
const globalProcessingUrls = new Set<string>();

export const useOptimizedSignedImages = (imageUrls: (string | undefined)[]): Record<string, UseSignedImageResult> => {
  const [results, setResults] = useState<Record<string, UseSignedImageResult>>({});
  const isInitialized = useRef(false);

  const initializeResults = useCallback(() => {
    const validUrls = imageUrls.filter((url): url is string => Boolean(url));
    const initialResults: Record<string, UseSignedImageResult> = {};

    validUrls.forEach(url => {
      if (globalSignedUrlCache.has(url)) {
        initialResults[url] = globalSignedUrlCache.get(url)!;
      } else if (!globalProcessingUrls.has(url)) {
        initialResults[url] = { signedUrl: null, loading: true, error: null };
      }
    });

    setResults(initialResults);
  }, [imageUrls]);

  const generateSignedUrls = useCallback(async (urlsToProcess: string[]) => {
    if (urlsToProcess.length === 0) return;

    // Mark URLs as being processed
    urlsToProcess.forEach(url => globalProcessingUrls.add(url));

    await Promise.all(
      urlsToProcess.map(async (url) => {
        try {
          const { bucket, key } = parseS3Url(url);
          const signed = await getSignedUrl(key, bucket, 3600);
          
          const result = { signedUrl: signed, loading: false, error: null };
          globalSignedUrlCache.set(url, result);
          
          setResults(prev => ({
            ...prev,
            [url]: result
          }));
        } catch (err) {
          console.error(`Error generating signed URL for ${url}:`, err);
          const errorResult = { 
            signedUrl: null, 
            loading: false, 
            error: err instanceof Error ? err.message : 'Failed to load image' 
          };
          globalSignedUrlCache.set(url, errorResult);
          
          setResults(prev => ({
            ...prev,
            [url]: errorResult
          }));
        } finally {
          globalProcessingUrls.delete(url);
        }
      })
    );
  }, []);

  useEffect(() => {
    const validUrls = imageUrls.filter((url): url is string => Boolean(url));
    
    // Initialize results with cached data
    if (!isInitialized.current) {
      initializeResults();
      isInitialized.current = true;
    }

    // Find URLs that need to be processed
    const urlsToProcess = validUrls.filter(url => 
      !globalSignedUrlCache.has(url) && !globalProcessingUrls.has(url)
    );

    if (urlsToProcess.length > 0) {
      generateSignedUrls(urlsToProcess);
    }
  }, [imageUrls, initializeResults, generateSignedUrls]);

  return results;
};